// SUMMARY: Server-side locale resolution (cookie → Accept-Language → default) and the switch action

'use server'

import { cookies, headers } from 'next/headers'
import { DEFAULT_LOCALE, LOCALE_COOKIE, LOCALES, isLocale, type Locale } from './config'

const ONE_YEAR_SECONDS = 60 * 60 * 24 * 365

export async function getUserLocale(): Promise<Locale> {
	const cookieLocale = (await cookies()).get(LOCALE_COOKIE)?.value
	if (isLocale(cookieLocale)) return cookieLocale

	const acceptLanguage = (await headers()).get('accept-language')
	return pickLocaleFromAcceptLanguage(acceptLanguage)
}

export async function setUserLocale(locale: Locale): Promise<void> {
	if (!isLocale(locale)) return
	;(await cookies()).set(LOCALE_COOKIE, locale, { path: '/', maxAge: ONE_YEAR_SECONDS, sameSite: 'lax' })
}

// EXAMPLE: "en-US,en;q=0.9,de;q=0.8" → "en"
function pickLocaleFromAcceptLanguage(header: string | null): Locale {
	if (!header) return DEFAULT_LOCALE

	const preferred = header
		.split(',')
		.map((part) => {
			const [tag, quality] = part.trim().split(';q=')
			return { language: (tag ?? '').toLowerCase().split('-')[0] ?? '', quality: Number(quality ?? '1') }
		})
		.sort((a, b) => b.quality - a.quality)

	for (const candidate of preferred) {
		const match = LOCALES.find((locale) => locale === candidate.language)
		if (match) return match
	}
	return DEFAULT_LOCALE
}
